import {Template, Component, Attribute} from '@scoutgg/ftw/source/lib'
import template from './events.jade'
import {Match, Event} from '../../models'
import getStateOld from '../../services/create-event'
import getState from '../../services/event-state'
import { eventNames } from '../../services/event-state'
import debounce from 'lodash/debounce'
import clamp from '@scoutgg/ftw/source/lib/clamp'
import '@scoutgg/ftw/source/atoms/button/button'
import '@scoutgg/ftw/source/atoms/tooltip/tooltip'
import '@scoutgg/ftw/source/atoms/icons/icons'
import { prompt } from '@scoutgg/ftw/source/services/modal'
import { relativeTimeThreshold } from 'moment'
import notify from '@scoutgg/ftw/source/services/notification'

const BALL_OUT_OF_PLAY = 'ball_out_of_play'
const MAX_VISIBLE_EVENTS = 32
const LAZY_LOAD = false
let useWhitelist = true
const whitelists = {
  csgo: [
    'round_ended',
    'half_ended',
    'half_started',
    'round_started',
    'match_started',
    'match_ended',
    'match_paused',
    'five_kills',
    'time_expired',
    'bomb_defused',
    'map_start',
    'map_win',
    'map_ended',
    'map_end',
    'target_bombed',
  ],
}
@Component('sporty')
@Template(template)
@Attribute('match', Match)
@Attribute('validity', Boolean)
@Attribute('filterbop', Boolean)
@Attribute('context', String)
export default class Events extends HTMLElement {
  created() {
    this.eventIndexBounds = [0, MAX_VISIBLE_EVENTS]
    this.cleanSheetExpanded = true
  }
  async attached() {
    await this.matchChange()
    this.debounceRender = debounce(() => this.render(), 100)
    if(LAZY_LOAD) this.eventScrollObserve()
  }

  detached() {
    this.eventsObserver?.disconnect()
  }

  get mainEvents() {
    const state = (this.match.lockedEvents || []).length
      ? 'removed'
      : 'unchanged'
    const filtered = []
    let events = (this.match.events || [])
      .map(event => {
        if(event.subEvent) filtered.push(event.subEvent)
        return {
          event: event,
          state: state,
        }
      })
      .filter(item => !filtered.includes(item.event))
    const existing = events.reduce((obj, item) => {
      obj[item.event] = item
      return obj
    }, {})
    if(this.match.stopFeeds) {
      for(const event of (this.match.lockedEvents || [])) {
        if(existing[event]) {
          existing[event].state = 'unchanged'
        } else {
          events.push({
            event: event,
            state: 'added',
          })
        }
      }
    }

    const normalSort = (a, b) => {
      return (
        b.seconds - a.seconds ||
        b.millisecond - a.millisecond ||
        (b.name || '').localeCompare(a.name || '') ||
        ((b.player && b.player.name) || '')
          .localeCompare((a.player && a.player.name) || '')
      )
    }
    const minuteSort = (a, b) => {
      return (
        b.minute - a.minute ||
        b.millisecond - a.millisecond ||
        b.seconds - a.seconds
      )
    }
    if(whitelists[this.match.sport] && useWhitelist) {
      events = events
        .filter(z => whitelists[this.match.sport].includes(z.event.name))
    }
    return events
      .filter(e =>
        !(this.filterbop && e.event.name === BALL_OUT_OF_PLAY ) &&
        !e.event.mainEvent
        )
      .sort((a, b) => {
        a = a.event
        b = b.event
        if(['csgo', 'dota2', 'lol'].includes(this.sport)) {
          return minuteSort(a, b)
        }
        return normalSort(a, b)
      })
  }

  get whitelists() {
    return whitelists
  }
  eventScrollObserve() {
    this.eventsObserver = new IntersectionObserver((records, observer) => {
      for(const record of records) {
        const targetInfo = record.boundingClientRect
        const index = +(record.target.dataset?.index || 0)
        if(record.isIntersecting) {
          if(index > this.eventIndexBounds[1]) {
            this.eventIndexBounds[0] = clamp(0, index - MAX_VISIBLE_EVENTS, index)
            this.eventIndexBounds[1] = index
          } else {
            if(this.eventIndexBounds[1] <= MAX_VISIBLE_EVENTS) {
              this.eventIndexBounds[0] = 0
              this.eventIndexBounds[1] = clamp(
                MAX_VISIBLE_EVENTS,
                index,
                index + MAX_VISIBLE_EVENTS
              )
            } else {
              if(index <= this.eventIndexBounds[0]) {
                this.eventIndexBounds[0] = index
              } else {
                this.eventIndexBounds[0] = clamp(
                  0,
                  index - MAX_VISIBLE_EVENTS,
                  MAX_VISIBLE_EVENTS
                )
              }
              this.eventIndexBounds[1] = clamp(
                MAX_VISIBLE_EVENTS,
                index + (MAX_VISIBLE_EVENTS / 2),
                index + (MAX_VISIBLE_EVENTS / 2)
              )
            }
          }
        }
        this.debounceRender()
      }
    }, { threshold: 1.0, rootMargin: '0px', })

    const events = this.shadowRoot.querySelectorAll('.event-wrapper')
    events.forEach(ev => {
      this.eventsObserver.observe(ev)
    })
  }

  isVisible(index) {
    return (
      !LAZY_LOAD ||
      (
        (index >= this.eventIndexBounds[0]) &&
        (index <= this.eventIndexBounds[1])
      )
    )
  }

  async matchChange() {
    getState(this.match).listen(this, true)
    getStateOld(this.match).listen(this, true)
    await this.update(this.match.load())
    return this.match
  }
  edit(event) {
    getStateOld(this.match).assignOriginal(event)
    getState(this.match).assignOriginal(event)
  }
  async destroy(event) {
    await this.match.post('pull-event', { eventId: event._id })
    getStateOld(this.match).reload(true)
  }
  invalidate(event) {
    event.validity = 'invalid'
    event.save().then(() => { getStateOld(this.match).reload() })
  }
  validate(event) {
    event.validity = 'valid'
    event.save().then(() => { getStateOld(this.match).reload() })
  }

  subEvents(event) {
    return this.match.events.filter(e => {
      return e.mainEvent === event || event.subEvent === e
    })
  }

  toggleCleanSheet() {
    this.cleanSheetExpanded = !this.cleanSheetExpanded
    this.render()
  }

  sendFeeds() {
    this.match.post('send-feeds')
      .then(() => { notify('Events sent', { level: 'success', timeout: 5000 }) })
      .catch((err) => {
        notify(err.message, { level: 'danger', timeout: 10000 })
      })
      .then(() => this.render())
    this.render()
  }

  async confirm(event) {
    const answer = await prompt(`
        Event confirmations can not be taken back once sent. Proceed?
      `, { type: 'yesno' })
    if(!answer) return
    event.validity = 'confirmed'
    const subEvents = this.subEvents(event)
    if(subEvents.length) {
      for(const subEvent of subEvents) {
        subEvent.validity = 'confirmed'
        await event.save({ subEvent })
      }
    } else {
      await event.save()
    }
    await this.sendFeeds()
    this.render()
    await this.matchChange()
  }

  async unconfirm(event) {
    const answer = await prompt(`
    Confirmed events should normally never be unconfirmed.
    Do you still want to do it?.
  `,{ type: 'yesno' })
    if(!answer) return
    event.validity = 'valid'
    const subEvents = this.subEvents(event)
    if(subEvents && subEvents.length) {
      for(const subEvent of subEvents) {
        subEvent.validity = 'valid'
        await event.save({ subEvent })
      }
    } else {
      await event.save()
    }
    this.render()
    await this.matchChange()
  }

  toggleWhitelist() {
    useWhitelist = !useWhitelist
    this.render()
  }

  modifyEvent(type) {
    return (ev, el) => {
      var event = Event.instance(el.parentElement.parentElement.dataset.event)
      this.emit(type + ':event', {event: event})
      if(type === 'delete') {
        for(const subEvent of this.subEvents(event)) {
          this.remove(subEvent)
        }
        this.remove(event)
      }
      this.render()
    }
  }
  otherSide(event) {
    return event.side === 'home' ? 'away' : 'home'
  }
  prettyName(event) {
    if(!event.name) return 'no_name'
    if(event.name.indexOf('substitution') === 0) {
      return 'Substitution ' + ['On', 'Off'][event.name.slice(-1)]
    }
    if(event.name === 'curling_end') return `End ${event.minute}`
    if(event.name === 'plus') return '+'
    if(event.name === 'minus') return '-'
    if(event.name === 'goal' && event.subType) {
      if(event.subType === 'power') return 'Power Play Goal'
      if(event.subType === 'box') return 'Box Play Goal'
    }
    if(event && event.name === 'assist' && event.mainEvent && event.mainEvent.subType) {
      if(event.mainEvent.subType === 'power') return 'Power Play Assist'
      if(event.mainEvent.subType === 'box') return 'Box Play Assist'
      if(event.mainEvent.subType === 'assist_fantasy') return 'Assist Fantasy'
      if(event.mainEvent.subType === 'tbi') return 'Assist Tbi'
    }
    return event.name
      .split('_')
      .map(p => p[0].toUpperCase() + p.slice(1))
      .join(' ')
  }
  prettyDuration(event) {
    if(event.name === 'curling_end') return `${event.duration}pts`
    if(event.name === 'extras') return `${event.duration} runs`
    if(this.sport === 'csgo') return ''
    return `${event.duration}m`
  }
  remove(event) {
    var index = this.events.indexOf(event)
    if(index < 0) return
    this.events.splice(index, 1)
  }
  translatedMinute(event) {
    if(this.sport === 'csgo') {
      const withoutBatch = event.minute - 1000 * event.batch
      if(event.name === 'map_win') return 'M' + event.batch + ' END'
      return 'R:' + Math.floor(withoutBatch / 10).toString() + ' T:' + ((withoutBatch) % 10).toString() + '.' + event.second
    }
  }

  get showMinute() {
    return ![
      'curling',
    ].includes(this.sport)
  }
  get useWhitelist() {
    return useWhitelist
  }
  get translateMinute() {
    return ['csgo'].includes(this.sport)
  }
  get sport() {
    return this.match && this.match.sport
  }
}
